import React, { useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { Loader2, CheckCircle, Clock, ClockIcon } from "lucide-react";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { DateTimeUtil } from "../../utils";
import { NgwAvatar } from "../../components/common";
import { ApprovalHistory } from "../../components/notificationv2";
import { callPhpWorkflow } from "../../redux/features/common";
import { getNotificationDetail } from "../../redux/features/notifications";

const OpenShiftNotificationDetail: React.FC = () => {
    const notificationDetail = useSelector((state: RootState) => state.notification.notificationDetail);
    const user = useSelector((state: RootState) => state.user.user);
    const isLoading = useSelector((state: RootState) => state.notification.isNotificationDetailLoading);
    const dispatch = useAppDispatch();
    const [notes, setNotes] = useState("");
    const isCallingWorkflow = useSelector((state: RootState) => state.common.isCallingWorkflow);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Open Shift"));
    }, [dispatch]);

    // Extract open shift details from notification
    const openShiftDetails = notificationDetail?.open_shift_details;
    const requestor = openShiftDetails?.claimed_requestor;
    const releasedRequestor = openShiftDetails?.released_requestor;
    
    // Helper function to get claim requestor data with consistent property names
    // This handles the API inconsistency where claim_requestor.employee_id might not match claim_request_emp_account_id
    // We use claim_request_emp_account_id as the source of truth and fallback to workflow details if needed
    // const getrequestor = (): { employee_id?: string; emp_name?: string; image_url?: string } | null => {
    //     // First try to use claim_requestor if it has the correct employee_id
    //     if (openShiftDetails?.claimed_requestor?.employee_id && 
    //         openShiftDetails?.claim_request_emp_account_id &&
    //         openShiftDetails.claimed_requestor.employee_id === openShiftDetails.claim_request_emp_account_id) {
    //         return {
    //             employee_id: openShiftDetails.claimed_requestor.employee_id,
    //             emp_name: openShiftDetails.claimed_requestor.emp_name,
    //             image_url: openShiftDetails.claimed_requestor.image_url
    //         };
    //     }
        
    //     // Fallback to workflow details actor_json
    //     if (openShiftDetails?.claim_request_emp_account_id) {
    //         const actorData = notificationDetail?.workflow_details?.[0]?.actor_json?.[openShiftDetails.claim_request_emp_account_id];
    //         if (actorData) {
    //             return {
    //                 employee_id: actorData.employee_id,
    //                 emp_name: actorData.name, // actor_json uses 'name' not 'emp_name'
    //                 image_url: actorData.image_url
    //             };
    //         }
    //     }
        
    //     return null;
    // };
    

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
            </div>
        );
    }

    const getCurrentStep = () => {
        const currentStepCode = notificationDetail?.workflow_current_step_details?.step_code;

        return notificationDetail?.workflow_details?.filter((wf) => wf.step_code === currentStepCode)?.[0];
    };

    const handleApprove = async () => {
        const open_shift_details = notificationDetail?.open_shift_details;
        if (!open_shift_details) return;

        const openShiftRequestId = open_shift_details?.os_sch_open_shift_transactions_id;
        // const employeeId = open_shift_details?.requestor?.employee_id;
        const employeeId = requestor?.employee_id!;

        if (!openShiftRequestId || !employeeId) return;

        const body = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 1,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: openShiftRequestId,
            result: { APPROVED: { [employeeId]: [openShiftRequestId] }, REJECTED: {} },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();
        // navigate(-1);
    };

    const handleReject = async () => {
        const openShiftDetails = notificationDetail?.open_shift_details;
        if (!openShiftDetails) return;

        const openShiftRequestId = openShiftDetails?.os_sch_open_shift_transactions_id;
        // const employeeId = openShiftDetails?.requestor?.employee_id;
        const employeeId = requestor?.employee_id!;

        if (!openShiftRequestId || !employeeId) return;

        const body = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 0,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: openShiftRequestId,
            result: { APPROVED: {}, REJECTED: { [employeeId]: [openShiftRequestId] } },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();

        // navigate(-1);
    };

    const refreshData = async () => {
        setIsRefreshing(true);
        await dispatch(
            getNotificationDetail({
                notificationId: notificationDetail?.notification_id!,
                employeeId: Number(user.os_emp_account_id!),
            })
        );
        setIsRefreshing(false);
    };

    const CurrentApproversSection = () => {
        return <ApprovalHistory workflowDetails={notificationDetail?.workflow_details} title="Approval History" />;
    };

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">
                {/* Employee Info Cards */}
                <div className="mx-4 mt-4">
                    {/* Mobile Layout - Vertical Stack */}
                    <div className="block md:hidden space-y-3">
                        {/* Requestor Card (Who released the shift) */}
                        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-blue-500">
                            <div className="flex items-start space-x-3">
                                <div className="w-12 h-12 relative">
                                    <NgwAvatar src={releasedRequestor?.image_url} name={releasedRequestor?.emp_name} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                            RELEASED
                                        </span>
                                    </div>
                                    <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                        {releasedRequestor?.emp_name}
                                    </h2>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        Originally owned this shift
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Transfer Arrow for Mobile */}
                        <div className="flex justify-center text-gray-400">
                            <div className="flex flex-col items-center">
                                <svg className="w-6 h-6 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                                <span className="text-xs text-gray-500 mt-1">Shift Transfer</span>
                            </div>
                        </div>

                        {/* Claim Requestor Card (Who claimed the shift) */}
                        {requestor && (
                            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-green-500">
                                <div className="flex items-start space-x-3">
                                    <div className="w-12 h-12 relative">
                                        <NgwAvatar src={requestor?.image_url} name={requestor?.emp_name} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                CLAIMED
                                            </span>
                                        </div>
                                        <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                            {requestor?.emp_name}
                                        </h2>
                                        <p className="text-gray-600 text-sm leading-relaxed">
                                            Now owns this shift
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Desktop Layout - Horizontal Flow */}
                    <div className="hidden md:flex items-center justify-between">
                        {/* Requestor Card (Who released the shift) - Left side */}
                        <div className="bg-white rounded-lg shadow-sm p-4 flex-1 mr-2 border-l-4 border-l-blue-500">
                            <div className="flex items-start space-x-3">
                                <div className="w-12 h-12 relative">
                                    <NgwAvatar src={releasedRequestor?.image_url} name={releasedRequestor?.emp_name} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                            RELEASED
                                        </span>
                                    </div>
                                    <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                        {releasedRequestor?.emp_name}
                                    </h2>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        Originally owned this shift
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Arrow showing transfer */}
                        <div className="flex flex-col items-center mx-2 text-gray-400">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            <span className="text-xs text-gray-500 mt-1">Shift Transfer</span>
                        </div>

                        {/* Claim Requestor Card (Who claimed the shift) - Right side */}
                        {requestor && (
                            <div className="bg-white rounded-lg shadow-sm p-4 flex-1 ml-2 border-l-4 border-l-green-500">
                                <div className="flex items-start space-x-3">
                                    <div className="w-12 h-12 relative">
                                        <NgwAvatar src={requestor?.image_url} name={requestor?.emp_name} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                CLAIMED
                                            </span>
                                        </div>
                                        <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                            {requestor?.emp_name}
                                        </h2>
                                        <p className="text-gray-600 text-sm leading-relaxed">
                                            Now owns this shift
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Overall Status Card */}
                <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3 mt-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="mb-3 md:mb-0">
                            <h3 className="text-lg font-semibold text-gray-900">Shift Status</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Current state of the open shift
                            </p>
                        </div>
                        <div className="text-left md:text-right">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${
                                openShiftDetails?.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                                openShiftDetails?.status === 'published' ? 'bg-blue-100 text-blue-700' :
                                openShiftDetails?.status === 'picked' ? 'bg-yellow-100 text-yellow-700' :
                                openShiftDetails?.status === 'approved' ? 'bg-green-100 text-green-700' :
                                openShiftDetails?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                openShiftDetails?.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                                'bg-gray-100 text-gray-700'
                            }`}>
                                {openShiftDetails?.status === 'draft' ? 'Draft' :
                                 openShiftDetails?.status === 'published' ? 'Published' :
                                 openShiftDetails?.status === 'picked' ? 'Picked' :
                                 openShiftDetails?.status === 'approved' ? 'Approved' :
                                 openShiftDetails?.status === 'rejected' ? 'Rejected' :
                                 openShiftDetails?.status === 'cancelled' ? 'Cancelled' :
                                 'Unknown'}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                                {openShiftDetails?.status === 'draft' ? 'Shift is being prepared' :
                                 openShiftDetails?.status === 'published' ? 'Shift is available for claiming' :
                                 openShiftDetails?.status === 'picked' ? 'Shift has been claimed' :
                                 openShiftDetails?.status === 'approved' ? 'Shift transfer approved' :
                                 openShiftDetails?.status === 'rejected' ? 'Shift transfer rejected' :
                                 openShiftDetails?.status === 'cancelled' ? 'Shift transfer cancelled' :
                                 'Status unknown'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shift Details Card */}
                <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3">
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                                <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Shift Schedule
                            </h3>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-1 break-words">
                                    {DateTimeUtil.formatDateTimeRangeUtc(
                                        `${openShiftDetails?.shift_date} ${openShiftDetails?.shift_start_time}`,
                                        `${openShiftDetails?.shift_date} ${openShiftDetails?.shift_end_time}`,
                                        "Invalid Date provided",
                                        true
                                    )}
                                </h4>
                                <p className="text-gray-600 text-sm">{openShiftDetails?.shift_name || "Main Office"}</p>
                            </div>
                        </div>
                    </div>

                    <CurrentApproversSection />

                    {/* Add note textbox */}
                    {getCurrentStep() &&
                        getCurrentStep()?.current_status == "0" &&
                        getCurrentStep()?.actor_json?.[user.id!] &&
                        notificationDetail?.only_notify_no_action !== "1" && (
                            <div className="mt-4">
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full border-2 rounded-md p-2"
                                    placeholder="Add notes"
                                />
                            </div>
                        )}

                    {getCurrentStep() &&
                        getCurrentStep()?.current_status == "0" &&
                        getCurrentStep()?.actor_json?.[user.id!] && 
                        notificationDetail?.only_notify_no_action !== "1" && (
                            <div>
                                {isCallingWorkflow ? (
                                    <Loader2 className="animate-spin text-green-500" />
                                ) : (
                                    <div className="flex space-x-2 mt-4">
                                        <button
                                            onClick={handleApprove}
                                            className="flex-1 bg-white border border-blue-600 hover:bg-blue-50 text-blue-600 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={handleReject}
                                            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                        >
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

export default OpenShiftNotificationDetail;
